import { EssayPeriod } from '../../components/essays/essayPeriod';
import { QuarterPeriod } from '../../components/essays/quarterPeriod';
import type { CompanyModule } from '../../hooks/useCompanyModules';

/**
 * Дело на главной: что-то, чего ждут от человека и что он может сделать
 * прямо сейчас. Не уведомление и не новость — у каждого дела есть адрес,
 * куда вести, и срок, если он есть.
 */
export interface TodoItem {
  id: string;
  title: string;
  /** Уточнение под заголовком: какая именно договорённость, какой отчёт. */
  meta?: string;
  /** Срок словами: «14 октября». Пусто — срока нет. */
  due?: string;
  /** `now` — срок уже прошёл или горит сегодня, `soon` — на подходе. */
  urgency: 'overdue' | 'now' | 'calm';
  to: string;
}

export interface TodoInput {
  /** Отчётный месяц и состояние эссе за него. */
  essay: { period: EssayPeriod; status: 'missing' | 'draft' | 'returned' | 'submitted' };
  /** Отчётный квартал и состояние квартального отчёта. */
  quarterly: { period: QuarterPeriod; status: 'missing' | 'draft' | 'returned' | 'submitted' };
  /** Сколько отчётов ждут моей оценки и до какого дня открыто окно. */
  scoring: { pending: number; lastDay: string | null; isWindowOpen: boolean };
  /** Self-эссе активного раунда, если он идёт и стадия — сбор эссе. */
  review: { selfPending: boolean; peerPending: number; stage?: string };
  /** Просроченные договорённости с 1-on-1. */
  goals: { overdue: number; firstTitle?: string };
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Список дел строится здесь, а не в компоненте: это правила продукта
 * («вернули на доработку важнее несданного»), и они должны проверяться
 * тестом, а не глазами на живом экране.
 *
 * Порядок — не приоритет по красоте, а по стоимости пропуска: просроченное
 * и возвращённое сверху, спокойное снизу.
 */
/** Раздел дела: дела неподключённых компании разделов не показываем. */
const TODO_MODULE: Record<string, CompanyModule> = {
  'goals-overdue': 'goals',
  'essay-returned': 'essays',
  'essay-missing': 'essays',
  'quarterly-returned': 'quarterly',
  'quarterly-draft': 'quarterly',
  scoring: 'quarterly',
  'review-self': 'performanceReview',
  'review-peer': 'performanceReview',
};

export function buildTodo(
  input: TodoInput,
  t: Translate,
  monthName: string,
  hasModule: (key: CompanyModule) => boolean = () => true,
): TodoItem[] {
  return collectTodo(input, t, monthName).filter((item) => hasModule(TODO_MODULE[item.id]));
}

function collectTodo(input: TodoInput, t: Translate, monthName: string): TodoItem[] {
  const items: TodoItem[] = [];

  if (input.goals.overdue > 0) {
    items.push({
      id: 'goals-overdue',
      title: t('dashboard.todo.goalsOverdue', { count: input.goals.overdue }),
      meta: input.goals.firstTitle,
      urgency: 'overdue',
      to: '/profile/goals',
    });
  }

  if (input.essay.status === 'returned') {
    items.push({
      id: 'essay-returned',
      title: t('dashboard.todo.essayReturned', { month: monthName }),
      urgency: 'overdue',
      to: '/essays',
    });
  } else if (input.essay.status !== 'submitted') {
    items.push({
      id: 'essay-missing',
      title: t(
        input.essay.status === 'draft'
          ? 'dashboard.todo.essayDraft'
          : 'dashboard.todo.essayMissing',
        { month: monthName },
      ),
      urgency: 'now',
      to: '/essays',
    });
  }

  if (input.quarterly.status === 'returned') {
    items.push({
      id: 'quarterly-returned',
      title: t('dashboard.todo.quarterlyReturned', { quarter: input.quarterly.period.quarter }),
      urgency: 'overdue',
      to: '/essays?mode=quarter',
    });
  } else if (input.quarterly.status === 'draft') {
    items.push({
      id: 'quarterly-draft',
      title: t('dashboard.todo.quarterlyDraft', { quarter: input.quarterly.period.quarter }),
      urgency: 'now',
      to: '/essays?mode=quarter',
    });
  }

  // Оценки принимают только внутри окна: вне его дело не сделать, и звать
  // человека некуда.
  if (input.scoring.isWindowOpen && input.scoring.pending > 0) {
    items.push({
      id: 'scoring',
      title: t('dashboard.todo.scoring', { count: input.scoring.pending }),
      due: input.scoring.lastDay ?? undefined,
      urgency: 'now',
      to: '/quarterly-scoring',
    });
  }

  if (input.review.selfPending) {
    items.push({
      id: 'review-self',
      title: t('dashboard.todo.reviewSelf'),
      urgency: 'now',
      to: '/performance-review',
    });
  }

  if (input.review.peerPending > 0) {
    items.push({
      id: 'review-peer',
      title: t('dashboard.todo.reviewPeer', { count: input.review.peerPending }),
      urgency: 'calm',
      to: '/performance-review',
    });
  }

  return items;
}
